import { Stack, Typography } from '@mui/material';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import SetupPhoneNumberMFA from './SetupPhoneNumber';
import ValidateMFA from './ValidateMFA';
import ViewState from './ViewState';
import { UserAttributes } from '../../networking/userInfo/User.types';
import { requestOneTimeCode, verifyAttributes } from '../../auth/authenticator';
import ContentBody from '../../components/ContentBody/ContentBody';
import SetupMFASuccess from './SetupMFASuccess';
import { MFASetupContext } from '../../common/mfaSetupContext';
import { ROUTES } from '../../routing/ROUTES';

const SetupMFA = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Credentials are provided via in-memory context rather than URL
  // path params to avoid leaking them to browser history, referrers,
  // analytics, screenshots, or other telemetry.
  const { credentials, clearCredentials } = useContext(MFASetupContext);

  // Capture the credentials present on mount. We intentionally do NOT
  // track context updates here so that clearing the context later
  // (e.g. after a successful enrollment) does not bounce the user
  // off the page while they are still seeing the success screen.
  const credentialsRef = useRef(credentials);
  const { username, password } = credentialsRef.current;

  const [errorMessage, setErrorMessage] = useState('');
  const [viewState, setViewState] = useState(ViewState.SetupPhoneNumber);

  // If the user lands on /setup-mfa directly (e.g., via bookmark or a
  // page refresh) there will be no credentials in context - send them
  // back to the login page instead of rendering an unusable form.
  useEffect(() => {
    if (!username || !password) {
      navigate(ROUTES.Logout);
    }
  }, [username, password, navigate]);

  // Wipe the transient credentials from memory as soon as this page
  // unmounts so they do not linger beyond the MFA setup flow.
  useEffect(() => {
    return () => {
      clearCredentials();
    };
  }, [clearCredentials]);

  const onPhoneNumberComplete = async (phoneNumber: string): Promise<void> => {
    const attributtes: UserAttributes = { phone_number: phoneNumber };
    const result = await requestOneTimeCode(username, password, attributtes);
    if (result.isSuccess) {
      setViewState(ViewState.VerifyMFA);
    } else {
      setErrorMessage(t('error.failedPhoneNumber'));
    }
  };

  const onMFAComplete = async (value: string): Promise<void> => {
    const attributtes: UserAttributes = { phone_number: value };
    const result = await verifyAttributes(attributtes);
    if (result.isSuccess) {
      setViewState(ViewState.MFASuccess);
    } else {
      throw result.error;
    }
  };

  const onBack = (): void => {
    setViewState(ViewState.SetupPhoneNumber);
  };

  return (
    <ContentBody>
      <Stack
        spacing={1}
        sx={{
          width: '750px',
          marginTop: '10px',
          marginLeft: '45px',
          marginBottom: '120px'
        }}
      >
        {viewState != ViewState.MFASuccess && (
          <>
            <Typography
              sx={{
                fontSize: '45px',
                color: 'primary.main'
              }}
            >
              {t('mfa.title')}
            </Typography>

            <Typography
              sx={{
                fontSize: '18px',
                color: 'text.primary'
              }}
            >
              {t('mfa.message')}
            </Typography>
          </>
        )}

        {viewState == ViewState.SetupPhoneNumber && (
          <SetupPhoneNumberMFA
            onComplete={onPhoneNumberComplete}
            errorMessage={{ errorMessage, setErrorMessage }}
          />
        )}
        {viewState == ViewState.VerifyMFA && (
          <ValidateMFA onComplete={onMFAComplete} onBack={onBack} />
        )}
        {viewState == ViewState.MFASuccess && <SetupMFASuccess />}
      </Stack>
    </ContentBody>
  );
};

export default SetupMFA;
